/**
 * CanvasSurfaceList — renders the list of active A2UI surfaces.
 *
 * Uses Google's A2UIRenderer for surface rendering. BeeOS-specific UI
 * (header, awareness, reference highlight, action buttons) wraps each surface.
 */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { A2UIRenderer, useA2UIActions, useA2UIState } from "@a2ui/react";
import type { EditorAwareness, SurfacePresentation } from "../types";
import { useCanvas, beeosRegistry } from "./provider";

export interface CanvasSurfaceListProps {
  readOnly?: boolean;
  showSurfaceHeader?: boolean;
  scrollWrapper?: (children: React.ReactNode) => React.ReactNode;
  renderHeader?: (count: number) => React.ReactNode;
  renderSurfaceActions?: (surfaceId: string) => React.ReactNode;
  className?: string;
  /** Empty-state copy when no surfaces exist yet (editable canvas). */
  emptyLabel?: string;
  /** Empty-state copy for the read-only canvas (waiting for content). */
  emptyReadOnlyLabel?: string;
}

interface SurfaceInfo {
  surfaceId: string;
  presentation: SurfacePresentation;
}

export function CanvasSurfaceList({
  readOnly = false,
  showSurfaceHeader,
  scrollWrapper,
  renderHeader,
  renderSurfaceActions,
  className,
  emptyLabel,
  emptyReadOnlyLabel,
}: CanvasSurfaceListProps) {
  const shouldShowSurfaceHeader = showSurfaceHeader ?? !readOnly;
  const {
    presentations,
    editorAwareness,
    canvasEnabled,
    setCanvasEnabled,
    clearCanvas,
    deleteSurface,
    referenceSurface,
    referencedSurfaceId,
  } = useCanvas();

  const { getSurfaces } = useA2UIActions();
  const { version } = useA2UIState();

  // Build surface list from Google's processor state
  const panelSurfaces = useMemo<SurfaceInfo[]>(() => {
    if (!canvasEnabled && !readOnly) return [];
    const all = getSurfaces();
    const result: SurfaceInfo[] = [];
    for (const [surfaceId, surface] of all) {
      if (surface.rootComponentId === null) continue;
      result.push({
        surfaceId,
        presentation: presentations.get(surfaceId) ?? "panel",
      });
    }
    return result;
  }, [version, getSurfaces, canvasEnabled, readOnly, presentations]);

  const AWARENESS_STALE_MS = 10_000;
  const surfaceEditors = useMemo(() => {
    const map = new Map<string, EditorAwareness[]>();
    const now = Date.now();
    for (const awareness of editorAwareness.values()) {
      if (now - awareness.timestamp > AWARENESS_STALE_MS) continue;
      const list = map.get(awareness.editingSurface) ?? [];
      list.push(awareness);
      map.set(awareness.editingSurface, list);
    }
    return map;
  }, [editorAwareness]);

  const [activelyEditing, setActivelyEditing] = useState<Set<string>>(() => new Set());
  const editTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const prevVersionRef = useRef<number>(0);
  const prevSurfaceIdsRef = useRef<Set<string>>(new Set());
  const surfaceRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    const currentIds = new Set(panelSurfaces.map((s) => s.surfaceId));

    if (version > prevVersionRef.current && prevVersionRef.current > 0) {
      const genuinelyNew: string[] = [];
      const updated: string[] = [];

      for (const s of panelSurfaces) {
        const isNew = !prevSurfaceIdsRef.current.has(s.surfaceId);
        if (isNew) {
          genuinelyNew.push(s.surfaceId);
        } else if (!activelyEditing.has(s.surfaceId)) {
          updated.push(s.surfaceId);
        }

        const existing = editTimersRef.current.get(s.surfaceId);
        if (existing) clearTimeout(existing);
        editTimersRef.current.set(
          s.surfaceId,
          setTimeout(() => {
            setActivelyEditing((prev) => {
              const next = new Set(prev);
              next.delete(s.surfaceId);
              return next;
            });
            editTimersRef.current.delete(s.surfaceId);
          }, 3000),
        );
      }

      const toAnimate = [...genuinelyNew, ...updated];
      if (toAnimate.length > 0) {
        setActivelyEditing((prev) => {
          const next = new Set(prev);
          for (const id of toAnimate) next.add(id);
          return next;
        });
      }

      if (genuinelyNew.length > 0) {
        requestAnimationFrame(() => {
          surfaceRefs.current.get(genuinelyNew[0]!)?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        });
      }
    }

    prevSurfaceIdsRef.current = currentIds;
    prevVersionRef.current = version;
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timers = editTimersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  if (!canvasEnabled && !readOnly) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-0 min-w-0 gap-4 text-muted-foreground px-6">
        <div className="rounded-full bg-muted/50 p-4">
          <SvgLayoutPanel className="h-8 w-8 text-muted-foreground/60" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-foreground/70">Canvas is disabled</p>
          <p className="text-xs text-muted-foreground">Enable to see visual content from the agent</p>
        </div>
        <button
          type="button"
          onClick={() => setCanvasEnabled(true)}
          className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-3 py-1.5 rounded-md border border-primary/20 hover:border-primary/40 hover:bg-primary/5"
        >
          Enable Canvas
        </button>
      </div>
    );
  }

  if (panelSurfaces.length === 0) {
    return (
      <div className="flex flex-col flex-1 min-h-0 min-w-0">
        {renderHeader ? renderHeader(0) : !readOnly && <DefaultHeader count={0} onClear={clearCanvas} />}
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 text-muted-foreground px-6">
          <div className="rounded-full bg-muted/40 p-3">
            <SvgLayers className="h-6 w-6 text-muted-foreground/50" />
          </div>
          <p className="text-xs text-muted-foreground/70">
            {readOnly
              ? (emptyReadOnlyLabel ?? "Waiting for canvas content...")
              : (emptyLabel ?? "No surfaces yet — ask the agent to show something")}
          </p>
        </div>
      </div>
    );
  }

  const hasActions = !!renderSurfaceActions || !readOnly;

  const surfaceCards = (
    <div className={readOnly ? "p-4 space-y-4" : "p-3 space-y-3"}>
      {panelSurfaces.map((surface) => {
        const isReferenced = !readOnly && referencedSurfaceId === surface.surfaceId;
        const surfaceEditorsList = surfaceEditors.get(surface.surfaceId);
        const hasActiveEditors = !!surfaceEditorsList && surfaceEditorsList.length > 0;
        const isEditing = activelyEditing.has(surface.surfaceId) || hasActiveEditors;
        return (
          <div
            key={surface.surfaceId}
            ref={(el) => {
              if (el) surfaceRefs.current.set(surface.surfaceId, el);
              else surfaceRefs.current.delete(surface.surfaceId);
            }}
            className={[
              "group relative rounded-xl border overflow-hidden transition-all backdrop-blur-sm",
              readOnly
                ? "bg-card shadow-[0_2px_8px_-2px_rgba(0,0,0,0.08),0_4px_16px_-4px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_8px_-2px_rgba(0,0,0,0.3),0_4px_16px_-4px_rgba(0,0,0,0.2)]"
                : "bg-card shadow-[0_1px_3px_-1px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.05)] dark:shadow-[0_1px_3px_-1px_rgba(0,0,0,0.25),0_2px_8px_-2px_rgba(0,0,0,0.15)]",
              isEditing
                ? "border-primary/60"
                : isReferenced
                  ? "ring-2 ring-primary border-primary/50"
                  : "border-border/60",
            ].join(" ")}
            style={isEditing ? PULSE_STYLE : undefined}
          >
            {(shouldShowSurfaceHeader || hasActions) && (
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/20 bg-muted/20 backdrop-blur-sm">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[10px] font-mono text-muted-foreground/60 truncate max-w-[120px]">
                    {shouldShowSurfaceHeader ? surface.surfaceId : ""}
                  </span>
                  {hasActiveEditors && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full shrink-0 surface-editor-badge">
                      <SvgPencil className="h-2.5 w-2.5" />
                      Editing
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-0.5">
                  {renderSurfaceActions?.(surface.surfaceId)}
                  {!readOnly && (
                    <>
                      <button
                        type="button"
                        onClick={() => referenceSurface(surface.surfaceId)}
                        className={[
                          "p-0.5 rounded transition-all",
                          isReferenced
                            ? "text-primary bg-primary/10"
                            : "opacity-0 group-hover:opacity-100 text-muted-foreground/50 hover:text-primary hover:bg-primary/10",
                        ].join(" ")}
                        title={isReferenced ? "Referenced for editing" : "Edit this surface"}
                      >
                        <SvgPencil className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteSurface(surface.surfaceId)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10"
                        title="Remove surface"
                      >
                        <SvgX className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            <div className={readOnly ? "p-4 min-w-0 w-full max-w-full overflow-hidden" : "p-3 min-w-0 w-full max-w-full overflow-hidden"}>
              <A2UIRenderer surfaceId={surface.surfaceId} registry={beeosRegistry} />
            </div>
          </div>
        );
      })}
    </div>
  );

  const scrollContent = scrollWrapper ? scrollWrapper(surfaceCards) : (
    <div className="flex-1 min-h-0 overflow-auto">{surfaceCards}</div>
  );

  const outerClass = ["flex flex-col flex-1 min-h-0 min-w-0", className].filter(Boolean).join(" ");

  return (
    <div className={outerClass}>
      <BeeOSStyleOverrides />
      {(activelyEditing.size > 0 || surfaceEditors.size > 0) && <PulseKeyframes />}
      {renderHeader
        ? renderHeader(panelSurfaces.length)
        : !readOnly && <DefaultHeader count={panelSurfaces.length} onClear={clearCanvas} />}
      {scrollContent}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal UI components
// ---------------------------------------------------------------------------

/**
 * BeeOS CSS overrides — neutralize Google A2UI component-specific styles that
 * conflict with our Tailwind + Radix UI components.
 *
 * Google injects rules like `.a2ui-surface .a2ui-card > section > *{height:100%}`
 * at specificity (0,2,1) which beats Tailwind utilities (0,1,0). We need matching
 * or higher specificity to restore natural flow layout.
 *
 * The `@layer a2ui-reset { :where(.a2ui-surface) :where(*) { all: revert } }`
 * is in a CSS layer with zero specificity — all Tailwind classes override it.
 * The real issue is the NON-layered component-specific styles.
 */
const BEEOS_OVERRIDES = `
/* Card: let children flow naturally instead of forced 100% height/width */
.a2ui-surface .a2ui-card > section { height: auto; overflow: visible; }
.a2ui-surface .a2ui-card > section > * { height: auto; }

/* Button children must inherit color from the <button> (which has
   text-primary-foreground). Without this, child Text components apply
   their own text-foreground class, turning the text dark/black. */
.a2ui-surface .a2ui-button button * { color: inherit; }

/* Checkbox: Google sets overflow:auto + min-height:0 which clips Radix
   <button> and breaks color rendering. Reset to visible. Also force
   color inheritance so the label and check icon resolve Tailwind vars. */
.a2ui-surface .a2ui-checkbox { overflow: visible; min-height: auto; background: transparent; }
.a2ui-surface .a2ui-checkbox * { color: inherit; }

/* TextField: Google sets display:flex but children don't fill. Force 100%. */
.a2ui-surface .a2ui-textfield { width: 100%; }

/* Ensure font-family inherits through component tree despite all:revert.
   :where() keeps zero specificity so explicit Tailwind font-* classes win. */
:where(.a2ui-surface) :where(*) { font-family: inherit; }

/* Chart: ensure chart container flows naturally within A2UI cards. */
.a2ui-surface .a2ui-chart.a2ui-chart { overflow: visible; height: auto; }
`;

const PULSE_KEYFRAMES = `
@keyframes surface-glow{0%,100%{box-shadow:0 0 0 1px hsl(var(--primary)/.5),0 0 8px 1px hsl(var(--primary)/.2);border-color:hsl(var(--primary)/.6);transform:translateY(0)}50%{box-shadow:0 0 0 1.5px hsl(var(--primary)/.25),0 0 14px 2px hsl(var(--primary)/.08);border-color:hsl(var(--primary)/.35);transform:translateY(-0.5px)}}
@keyframes badge-pulse{0%,100%{opacity:1}50%{opacity:.5}}
.surface-editor-badge{animation:badge-pulse 1.5s ease-in-out infinite}
`;
const PULSE_STYLE: React.CSSProperties = {
  animation: "surface-glow 2s ease-in-out infinite",
};

let beeosOverridesInjected = false;
function BeeOSStyleOverrides() {
  useEffect(() => {
    if (beeosOverridesInjected || typeof document === "undefined") return;
    beeosOverridesInjected = true;
    const style = document.createElement("style");
    style.id = "beeos-a2ui-overrides";
    style.textContent = BEEOS_OVERRIDES;
    document.head.appendChild(style);
  }, []);
  return null;
}

function PulseKeyframes() {
  return <style dangerouslySetInnerHTML={{ __html: PULSE_KEYFRAMES }} />;
}

function DefaultHeader({ count, onClear }: { count: number; onClear: () => void }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 border-b border-border/30 bg-background/80 backdrop-blur-sm shrink-0">
      <div className="flex items-center gap-2 min-w-0">
        <SvgLayoutPanel className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground truncate">Canvas</span>
        {count > 0 && (
          <span className="text-[10px] font-medium text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded-full shrink-0">
            {count}
          </span>
        )}
      </div>
      {count > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive transition-colors px-1.5 py-0.5 rounded hover:bg-destructive/5 shrink-0"
          title="Clear all surfaces"
        >
          <SvgTrash className="h-3 w-3" />
          <span>Clear</span>
        </button>
      )}
    </div>
  );
}

function SvgLayoutPanel({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect width="7" height="18" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" />
    </svg>
  );
}
function SvgLayers({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </svg>
  );
}
function SvgPencil({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    </svg>
  );
}
function SvgX({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}
function SvgTrash({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    </svg>
  );
}
